<template>
  <div class="col-12">
    <div class="d-flex profile-container">

      <AvatarImage :userId="sharedTest.user.uuid"/>

      <div class="p-2 d-flex flex-column" style="align-items: flex-start;">
        <span class="profile-name">{{patientName}}</span>
        <span class="profile-subtitle">{{$t(patientGender)}} - {{patientAge}} {{$t('more.post_year')}}</span>
      </div>

      <div class="ml-auto p-2 d-flex flex-column" style="align-items: flex-start;">
        <RadioButtonGroup v-if="!printing" name="user-label" :items="userLabels" :values="userLabelKeys" :selectedValue="patientLabel" :showSeperators="false" @valueChanged="userLabelValueChanged" style="display: inline-block;"/>
        <span v-if="printing" :id="'user-label-lbl-' + userLabelKeys.indexOf(patientLabel).toString()">{{$t(userLabels[userLabelKeys.indexOf(patientLabel)])}}</span>
      </div>

      <MiniToolbar v-if="!printing" class="p-2" :buttons="userToolbar" @clicked="userToolbarClicked"/>
    </div>

    <NewAnnotation v-if="can('shared.annotation', 'create') && !printing" ref="newAnnotationCtl" @canceled="annotationCanceled" @submitted="annotationSubmitted"/>

    <div v-if="last_annotation.length === 0">
      <DottedLineHeader text="">
        <div class="d-flex toolbar-container">
          <MiniToolbar v-if="!printing" class="p-2" :buttons="annotationViewToolbar"  additionalData="-" @clicked="annotationToolbarClicked" v-observe-visibility="reinitToolbar"/>
        </div>
      </DottedLineHeader>
      <div class="chart-height-message-container ml-4 mr-4">
        <span class="chart-height-message icon sli-bell"></span>
        <span class="chart-height-message-caption">{{$t("more.annotation_create_or_select_date")}}</span>
      </div>
    </div>

    <div v-for="(annotation, index) in last_annotation" :key="index">
      <DottedLineHeader :text="$t('more.annotation_date') + ': '" :value="annotation.created_at | jdateformat">
        <div class="d-flex toolbar-container">
          <MiniToolbar v-if="index === 0 && !printing" class="p-2" :buttons="annotationViewToolbar.concat(annotationToolbar)" :additionalData="annotation.uuid" @clicked="annotationToolbarClicked" v-observe-visibility="reinitToolbar"/>
          <MiniToolbar v-if="index > 0 && !printing" class="p-2" :buttons="annotationToolbar" :additionalData="annotation.uuid" @clicked="annotationToolbarClicked"/>
        </div>
      </DottedLineHeader>

      <p class="mt-5 mb-5 annotation">
        <pre style="text-align: start;">{{annotation.description}}</pre>
      </p>
    </div>
  </div>
</template>

<script>
// eslint-disable-next-line no-unused-vars
import persianDatePicker from 'persian-datepicker/dist/js/persian-datepicker'
import PersianDate from 'persian-date'
import Vue from 'vue'
import { mapState, mapActions } from 'vuex'
import {yearDifference, dayDifference} from '@/_helpers'
import DefaultDatePickerOptions from '@/components/DefaultDatePickerOptions.js'
import MiniToolbar from '@/components/MiniToolbar.vue'
import DottedLineHeader from '@/components/DottedLineHeader.vue'
import RadioButtonGroup from '@/components/RadioButtonGroup.vue'
import NewAnnotation from '@/components/NewAnnotation.vue'
import AvatarImage from '@/components/AvatarImage.vue'

export default {
  components: {
    MiniToolbar,
    DottedLineHeader,
    RadioButtonGroup,
    NewAnnotation,
    AvatarImage
  },
  props: {
    sharedTest: Object,
    printing: Boolean
  },
  data: () => ({
    annotationDateSelected: false,
    annotationUserDate: null,
    annotationsWithDate: [],
    annotationEditMode: false,
    annotationEditUuid: '',
    flags: 0,
    annotationFrom: '',
    annotationFromDate: -1,
    annotationToDate: -1,
    userLabelKeys: [
      'green',
      'yellow',
      'red'
    ],
    userLabels: [
      'more.green_label',
      'more.yellow_label',
      'more.red_label'
    ],
    userToolbar: [
      {
        icon: 'sli-printer',
        id: 'userinfo-pdf',
        tip: 'more.save_pdf'
      }
    ],
    annotationViewToolbar: [
      {
        icon: 'sli-clock',
        id: 'userinfo-history',
        tip: 'more.history'
      },
      {
        icon: 'sli-pin',
        tip: 'more.last_annotation'
      }
    ],
    unsubscribe: null
  }),
  computed: {
    ...mapState({
      annotationItems: state => state.test.annotations.items
    }),
    patientName () {
      if (!this.sharedTest) {
        return '...'
      }
      return this.sharedTest.user.first_name + ' ' + this.sharedTest.user.last_name
    },
    patientGender () {
      if (!this.sharedTest) {
        return '...'
      }
      return this.sharedTest.user.gender === 2 ? 'gender.male' : (this.sharedTest.user.gender === 1 ? 'gender.female' : 'gender.other')
    },
    patientAge () {
      if (!this.sharedTest) {
        return '...'
      }
      return yearDifference(this.sharedTest.user.birth_date)
    },
    patientLabel () {
      if (!this.sharedTest) {
        return null
      }
      return this.sharedTest.user_label ? this.sharedTest.user_label.label : null
    },
    last_annotation () {
      if (this.annotationDateSelected) {
        return this.annotationsWithDate
      }

      if (!this.sharedTest || !this.sharedTest.last_annotations || !this.sharedTest.last_annotations[0]) {
        return []
      }
      return this.sharedTest.last_annotations
    },
    calendarOptions () {
      var opts = DefaultDatePickerOptions(false)
      opts['onSelect'] = this.onDateSelected
      opts['monthPicker'] = {
        'onSelect': this.onMonthSelected
      }
      opts['yearPicker'] = {
        'onSelect': this.onYearSelected
      }
      opts['navigator'] = {
        'onPrev': this.onPrev,
        'onNext': this.onNext
      }
      return opts
    },
    annotationToolbar () {
      return [
        {
          icon: 'sli-pencil',
          tip: 'more.edit_annotation',
          small: true,
          visible: this.can('shared.annotation', 'create')
        },
        {
          icon: 'sli-trash',
          tip: 'more.remove_annotation',
          small: true,
          visible: this.can('shared.annotation', 'delete')
        }
      ]
    }
  },
  methods: {
    ...mapActions('test', ['annotations', 'newAnnotation', 'editAnnotation', 'removeAnnotation', 'userLabel']),
    reinitToolbar (isVisible, entry) {
      if (isVisible) {
        setTimeout(() => {
          this.initDatePicker()
        }, 100)
      }
    },
    userLabelValueChanged: function (label) {
      this.userLabel({userUuid: this.sharedTest.user.uuid, label})
    },
    annotationCanceled () {
      this.annotationEditUuid = ''
      this.annotationEditMode = false
    },
    annotationSubmitted (text) {
      if (this.annotationEditMode) {
        this.editAnnotation({annotationUuid: this.annotationEditUuid, annotation: text})
        this.annotationEditUuid = ''
        this.annotationEditMode = false
        return
      }

      this.newAnnotation({userUuid: this.sharedTest.user.uuid, annotation: text})
    },
    userToolbarClicked: function (id) {
      id = parseInt(id)
      switch (id) {
        case 0:
          this.$emit('savePDF')
          break
      }
    },
    annotationToolbarClicked: function (id, toolbarData, button) {
      switch (button.icon) {
        case 'sli-clock':
          // datepicker will show up and no need to write code here
          break
        case 'sli-pin':
          this.annotationDateSelected = false
          break
        case 'sli-pencil':
          // edit annotation
          this.annotationEditUuid = toolbarData
          this.annotationEditMode = true

          var a = this.last_annotation.filter(annotation => {
            if (annotation.uuid === toolbarData) {
              return true
            }
            return false
          })
          if (a.length === 0) {
            console.warn('no annotation for edit')
            return
          }

          this.$refs['newAnnotationCtl'].setTextValue(a[0].description)
          break
        case 'sli-trash':
          // remove annotation
          var r = confirm(this.$t('more.delete_confirm'))
          if (r === true) {
            this.removeAnnotation(toolbarData)
          }
          break
      }
    },
    onDateSelected: function (unixdate) {
      this.annotationDateSelected = true
      this.annotationUserDate = unixdate
      this.annotationsWithDate = []

      var date = new PersianDate(unixdate).format('YYYY,MM,DD')
      var fromDate = this.jalaliToTimeStamp(date)
      var toDate = this.jalaliToTimeStamp(date, false)

      this.annotationsWithDate = this.annotationItems.filter(annotation => {
        var res = annotation.created_at >= fromDate
        res = res && annotation.created_at <= toDate
        return res
      })
      this.annotationsWithDate.sort((a, b) => (a.created_at > b.created_at) ? -1 : ((b.created_at > a.created_at) ? 1 : 0))
    },
    onYearSelected: function (year) {
      this.getHighlightedDays()
    },
    onMonthSelected: function (monthIndex) {
      this.getHighlightedDays()
    },
    onNext: function (navigator) {
      this.getHighlightedDays()
    },
    onPrev: function (navigator) {
      this.getHighlightedDays()
    },
    showDataPicker: function (evt) {
      if (!this.sharedTest) {
        return
      }

      $('div[id|="persianDateInstance"]').remove()
      $('#userinfo-history').off('focus click')
      $('#userinfo-history').pDatepicker(this.calendarOptions)
      $('#userinfo-history').trigger('click')
      this.initDatePicker()
      this.getHighlightedDays()
    },
    getHighlightedDays: function () {
      setTimeout(() => {
        var days = $('td[data-date]:not(:has(span[class=\'other-month\']))')
        if (!days) {
          return
        }
        this.annotationFrom = days[0].dataset.date
        this.annotationFromDate = this.jalaliToTimeStamp(this.annotationFrom)
        this.annotationToDate = this.jalaliToTimeStamp(days[days.length - 1].dataset.date, false)
        this.annotations({userUuid: this.sharedTest.user.uuid, fromDate: this.annotationFromDate, toDate: this.annotationToDate})
      }, 500)
    },
    jalaliToTimeStamp: function (date, start = true) {
      var x = date.match(/(\d{0,4}),(\d{0,2}),(\d{0,2})/)
      if (x.length < 4) {
        // date not valid
        return
      }
      x = x.map(function (e) {
        return parseInt(e, 10)
      })
      if (start) {
        return new PersianDate([x[1], x[2], x[3]]).startOf('day').format('X')
      } else {
        return new PersianDate([x[1], x[2], x[3]]).endOf('day').format('X')
      }
    },
    initDatePicker: function () {
      $('#userinfo-history').off('focus click')
      $('#userinfo-history').on('focus click', (evt) => {
        this.showDataPicker(evt)
      })
    },
    highlightDaysOnDatePicker (annotationItems) {
      if (!annotationItems || annotationItems.length === 0) {
        return
      }
      var flags = 0
      var i = 0
      annotationItems.forEach(element => {
        var days = dayDifference(this.annotationFromDate, element.created_at)
        flags |= (1 << days)
        i++
      })

      this.flags = flags
      i = 0

      var x = this.annotationFrom.match(/(\d{0,4}),(\d{0,2}),(\d{0,2})/)
      if (!x || x.length < 4) {
        // date not valid
        return
      }

      while (flags > 0) {
        if (flags & 1) {
          var date = `${x[1]},${x[2]},${i}`
          $('td[data-date=\'' + date + '\'] span:first-child').addClass('highlighted-date')
        }

        flags /= 2
        i++
      }
    }
  },
  created () {
    var that = this
    this.unsubscribe = this.$store.subscribe((mutation, state) => {
      if (mutation.type === 'test/newAnnotationSuccess') {
        if (!this.sharedTest.last_annotations) {
          this.sharedTest.last_annotations = [ mutation.payload ]
        } else {
          this.sharedTest.last_annotations = [ mutation.payload, ...this.sharedTest.last_annotations ]
        }
      }
      if (mutation.type === 'test/removeAnnotationSuccess') {
        this.sharedTest.last_annotations = this.sharedTest.last_annotations.filter(elem => {
          if (elem.uuid === mutation.payload) {
            return false
          }
          return true
        })

        if (this.annotationDateSelected) {
          this.onDateSelected(this.annotationUserDate)
        }
      }
      if (mutation.type === 'test/editAnnotationSuccess') {
        let idx = -1
        this.sharedTest.last_annotations.forEach((elem, index) => {
          if (elem.uuid === mutation.payload.uuid) {
            idx = index
          }
        })
        if (idx >= 0) {
          Vue.set(this.sharedTest.last_annotations, idx, mutation.payload)
        }

        if (this.annotationDateSelected) {
          this.onDateSelected(this.annotationUserDate)
        }
      }
      if (mutation.type === 'test/annotationsSuccess') {
        that.highlightDaysOnDatePicker(mutation.payload)
      }
    })
  },
  mounted () {
    this.initDatePicker()
  },
  destroyed () {
    this.unsubscribe()
  }
}
</script>

<style>
.profile-container {
  align-items: center;
}
.profile-name {
  font-size: large;
  color: var(--result-patient-name);
}
.profile-subtitle {
  font-size: x-small;
  font-weight: lighter;
  color: var(--result-patient-age);
}
.section-title, .section-value {
  font-size: x-small;
  font-weight: bold;
}
.section-title {
  color: var(--dottedline-text-color);
}
.section-value {
  color: var(--dottedline-value-color);
}
.annotation {
  text-align: start;
}
.annotation pre {
  color: var(--annotation-text-color);
  overflow-x: hidden;
  overflow-y: hidden;
  white-space: pre-wrap;
  white-space: -moz-pre-wrap;
  white-space: -pre-wrap;
  white-space: -o-pre-wrap;
  word-wrap: break-word;
}
.highlighted-date {
  background-color: var(--highlighted-date-color) !important;
  border-radius: 4px !important;
}
#user-label-lbl-2 {
  color: var(--red-flag-color);
}
#user-label-lbl-1 {
  color: var(--yellow-flag-color);
}
#user-label-lbl-0 {
  color: var(--green-flag-color)
}
#user-label input[type="radio"]:checked+label {
  background-color: rgb(233, 232, 232);
  border-radius: 5px;
}
</style>



// WEBPACK FOOTER //
// src/views/TestProfileHeader.vue